import {
  ClientRegistrationData,
  ClientRegistrationEntities,
  ClientRegistrationDetail,
  ClientCurrentRate,
  ClientLocation,
  ClientPartner,
  ClientContact,
  ClientCod,
  ClientCodAsDefault,
  ClientTax,
  ClientBilling,
  BankDetail,
  clientPaymentMethodData,
  clientCodConfigAmountData,
  ClientDiscount,
  ClientRateHistory
} from "@/domain/entities/ClientRegistration";
import { OptionsClass } from "@/domain/entities/MainApp";
import { Pagination, PaginationV2 } from "@/domain/entities/Pagination";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { AxiosResponse } from "axios";
import startCase from "lodash/startCase";
import { ConfigBucketStorage } from "@/feature-flags/flags-config-bucket-storage";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import {
  ClientDetailUpdateItem,
  ClientDetailUpdate
} from "@/domain/entities/ClientManagement";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";

export class ClientRegistrationMapper {
  public convertClientRegistrationFromApi(
    result: AxiosResponse<any>
  ): ClientRegistrationEntities {
    const { data } = result;
    return new ClientRegistrationEntities(
      new Pagination(data.meta.page, data.meta.limit, data.meta.total_records),
      data.data.length > 0
        ? data.data.map((item: any) => {
            return new ClientRegistrationData({
              id: item.id,
              clientAlias: item.client_alias,
              clientName: item.company_name,
              clientCode: item.code,
              clientStartDate: item.start_date,
              clientEndDate: item.end_date,
              clientStatus: item.status,
              rateVersionStatus: item.rate_version_status || "",
              clientType: item.type
            });
          })
        : []
    );
  }

  private _mapClientLocation(data: any) {
    return new ClientLocation({
      id: data.client_city_id,
      code: data.client_city_code,
      name: data.client_city_name,
      cityCode: data.client_city_code
    });
  }

  private _mapClientDistrictLocation(data: any) {
    return new ClientLocation({
      id: data.client_district_id,
      code: data.client_district_code,
      name: data.client_district_name,
      cityCode: data.client_city_code,
      countryCode: data.client_country_code
    });
  }

  private _mapClientPartner(data: any) {
    return new ClientPartner({
      clientPartnerId: data.client_partner_id,
      clientPartnerName: data.client_partner_name,
      clientPartnerQuantifierCommission:
        data.client_partner_quantifier_commission,
      clientPartnerCommissionAmount: data.client_partner_commission_amount,
      clientPartnerNumeratorCommision: data.client_partner_numerator_commission
    });
  }

  private _mapClientContact(data: any) {
    return new ClientContact({
      clientContactName: data.client_contact_name,
      clientContactJobTitle: data.client_contact_job_title,
      clientContactEmail: data.client_contact_email,
      clientContactPhone: data.client_contact_phone.startsWith("0")
        ? data.client_contact_phone.substr(1)
        : data.client_contact_phone.substr(2)
    });
  }

  private _mapBasicClientData(data: any, isV1: boolean) {
    return {
      id: data.client_id,
      clientCode: data.client_code,
      clientCodeElexys: data.client_elexys_code,
      clientName: data.client_name,
      clientAlias: data.client_alias,
      clientPhone: data.client_phone.substr(2),
      clientEmail: data.client_email,
      clientType: data.client_type,
      clientCategory: data.client_category,
      clientCompanySize: startCase(data.client_company_size),
      clientTaxNumber: isV1
        ? data.client_tax_number
        : data.client_tax_number.replaceAll("-", "").replaceAll(".", ""),
      clientStatus: data.client_status,
      clientAddress: data.client_address,
      clientDistrict: this._mapClientDistrictLocation(data),
      clientCity: this._mapClientLocation(data),
      clientStatusUpdatedAt: data.client_status_updated_at,
      clientStatusUpdatedBy: data.client_status_updated_by,
      clientStatusUpdatedReason: data.client_status_updated_reason,
      clientParentId: data.client_parent_id,
      clientParentName: data.client_parent_name,
      clientPartner: this._mapClientPartner(data),
      clientContact: this._mapClientContact(data),
      clientCreatedAt: data.client_created_at,
      clientUpdatedAt: data.client_updated_at,
      clientCreatedBy: data.client_created_by,
      clientUpdatedBy: data.client_updated_by,
      clientPerusahaan: data.client_perusahaan ?? "",
      clientCountry: data.client_country_code ?? "",
      clientManagingCountryCode: data.client_managing_country_code ?? "",
      clientManagingCountryName: data.client_managing_country_name ?? ""
    };
  }

  private _mapClientCurrentRate(data: any) {
    return new ClientCurrentRate({
      clientCustomRateId: this.getSaveValueOrString(
        data.client_current_rate?.client_custom_rate_id
      ),
      clientStatusCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_status_custom_rate
      ),
      clientRateVersionStartDate:
        data.client_current_rate?.client_rate_version_start_date || new Date(0),
      clientRateVersionEndate:
        data.client_current_rate?.client_rate_version_end_date || new Date(0),
      clientStatusUpdatedAt: this.getSaveValueOrString(
        data.client_current_rate?.client_status_updated_at
      ),
      clientStatusUpdatedBy: this.getSaveValueOrString(
        data.client_current_rate?.client_status_updated_by
      ),
      clientStatusUpdatedReason: this.getSaveValueOrString(
        data.client_current_rate?.client_status_updated_reason
      ),
      clientVersionBaseRate: this.getSaveValueOrString(
        data.client_current_rate?.client_version_base_rate
      ),
      clientDiscountBaseRate:
        data.client_current_rate?.client_discount_base_rate || 0,
      clientFileNameBaseRate:
        data.client_current_rate?.client_file_name_base_rate || "-",
      clientFileBaseRate: this.getSaveValueOrString(
        data.client_current_rate?.client_file_base_rate
      ),
      clientVersionCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_version_custom_rate
      ),
      clientFileNameCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_file_name_custom_rate
      ),
      clientFileCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_file_custom_rate
      ),
      clientErrorNameCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_error_name_custom_rate
      ),
      clientErrorFileCustomRate: this.getSaveValueOrString(
        data.client_current_rate?.client_error_file_custom_rate
      ),
      clientStatusBulk: this.getSaveValueOrString(
        data.client_current_rate?.client_status_bulk
      ),
      clientRateVersionCustom3lc: this.getSaveValueOrString(
        data.client_current_rate?.client_rate_version_custom_3lc
      ),
      clientRatePriceType: this.getSaveValueOrString(
        data.client_current_rate?.client_rate_price_type
      ),
      clientRateInsuranceType: this.getSaveValueOrString(
        data.client_current_rate?.client_rate_insurance_type
      ),
      clientRateInsuranceValuePercentage:
        data.client_current_rate?.client_rate_insurance_value_percentage || 0,
      clientRateUpdatedAt: this.getSaveValueOrString(
        data.client_current_rate?.client_rate_updated_at
      ),
      discountProductTypes: data.client_current_rate?.discount_product_types
        ? data.client_current_rate?.discount_product_types.map(
            (d: any) => new ClientDiscount(convertSnakeToCamel(d))
          )
        : [],
      isStuck: data.client_current_rate?.is_stuck,
      useClientDiscountConfig:
        data.client_current_rate?.use_client_discount_config ?? true
    });
  }

  private _mapClientTax(data: any, isV1: boolean) {
    return new ClientTax({
      NIK: data.client_nik_id,
      NIKName: data.client_nik_name,
      NIKAddress: data.client_nik_address,
      isTaxPayer: data.client_efaktur_with_nik,
      taxNumber: isV1
        ? data.client_tax_number
        : data.client_tax_number.replaceAll(".", "").replaceAll("-", ""),
      taxPayerAddress: data.client_taxpayer_address,
      taxPayerName: data.client_taxpayer_name,
      transactionCode: data.client_tax_transaction_no,
      isEfakturWithNik: data.client_efaktur_with_nik,
      taxAttachmentUrl: parsingLinkGcpToAws(data.client_tax_attachment),
      taxAttachment: parsingLinkGcpToAws(data.client_tax_attachment),
      clientNikId: data.client_nik_id,
      clientNikAddress: data.client_nik_address,
      clientNikName: data.client_nik_name
    });
  }

  private _mapClientBilling(data: any) {
    return new ClientBilling({
      noWhatsAppFull: data.client_billing_wa_number,
      noWhatsApp: data.client_billing_wa_number.startsWith("0")
        ? data.client_billing_wa_number.substr(1)
        : data.client_billing_wa_number.substr(2),
      billingSchedule: data.client_billing_schedule_so.toLowerCase(),
      timeRangePayment: data.client_billing_payment_period.replace(" Days", ""),
      email: data.client_billing_email
        ? data.client_billing_email.split(",")
        : [],
      startDate: data.client_contract_start_date,
      endDate: data.client_contract_end_date,
      attachmentUrl: parsingLinkGcpToAws(data.client_contract_attachment_url),
      pksAttachmentUrl: parsingLinkGcpToAws(data.client_pks_attachment),
      pksAttachment: parsingLinkGcpToAws(data.client_pks_attachment)
    });
  }

  private _mapClientBankSettlement(data: any) {
    return new BankDetail({
      bankLabel: `VA ${startCase(data.client_va_bank_name)}`,
      bankName: data.client_va_bank_name,
      accountNo: data.client_va_bank_number,
      accountName: data.client_va_bank_account_name,
      logo: `${ConfigBucketStorage.config_bucket_bank_logo.getValue()}${
        data.client_va_bank_name
      }.png`
    });
  }

  public convertDetailClientRegistrationFromApi(
    result: AxiosResponse<any>,
    version = "v1"
  ): ClientRegistrationDetail {
    const {
      data: { data }
    } = result;

    const isV1 = version === "v1";

    return new ClientRegistrationDetail({
      ...this._mapBasicClientData(data, isV1),
      clientIsCustomRate:
        data.client_current_rate?.client_file_base_rate &&
        data.client_current_rate?.client_file_custom_rate
          ? "mix"
          : data.client_current_rate?.client_file_base_rate
          ? "basic"
          : "custom",
      clientIsRenewalCustomRate: data.client_is_renewal_custom_rate,
      clientIsCod: data.client_is_cod,
      clientIsDfod: data.client_is_dfod,
      clientIsMixpack: data.client_is_mixpack,
      clientIsDo: data.client_is_do,
      clientBranchDoPaymentType: data.client_branch_do_payment_type,
      clientCurrentRate: this._mapClientCurrentRate(data),
      clientRenewalRate: data.client_renewal_rate,
      clientCodReject: this._mapClientCodReject(data),
      clientCodRejectAsDefault: this._mapClientCodRejectAsDefault(data),
      clientBankName: data.client_beneficiary_account_name,
      clientBankNumber: data.client_beneficiary_account_number,
      clientBankEmail: data.client_beneficiary_email,
      clientBankLabel: data.client_beneficiary_bank_name,
      dataProcessed: data.data_processed_current,
      totalData: data.total_data_current,
      clientTax: this._mapClientTax(data, isV1),
      clientBilling: this._mapClientBilling(data),
      bankSettlement: this._mapClientBankSettlement(data),
      clientPadFee: data.client_pad_fee || 0,
      clientPaymentMethod: clientPaymentMethodData.find(
        (payment: OptionsClass) => payment.value === data.client_payment_method
      ),
      clientCodConfigAmount: clientCodConfigAmountData.find(
        (cod: OptionsClass) => cod.value === data.client_cod_config_amount
      ),
      clientCodShipmentDiscount: data.client_cod_shipment_discount,
      isReturnConfig: data.client_meta?.is_return_config || false,
      returnConfigLimit: data.client_meta?.return_config_limit || 0,
      returnConfigReasonIds: data.client_meta?.return_config_reason_ids || "",
      priceType: data.price_type,
      rateVersionCustom3lc: data.rate_version_custom_3lc,
      insuranceType: data.client_meta?.insurance_type || "",
      insuranceValuePercentage:
        data.client_meta?.insurance_value_percentage || "",
      dataProcessedCurrent: data.data_processed_current,
      dataProcessedRenewal: data.data_processed_renewal,
      totalDataCurrent: data.total_data_current,
      totalDataRenewal: data.total_data_renewal,
      clientIsNeedAssessment: data.client_is_need_assessment,
      detailUpdate: this._mapClientDetailUpdate(data),
      clientIsNeedSplitSo: data.client_is_need_split_so ?? false
    });
  }

  private _mapClientDetailUpdateKeyItem(data: any) {
    if (!data) return {};

    return Object.keys(data)
      .map((key) => {
        return {
          key,
          data: this._mapClientDetailUpdateItem(data[key])
        };
      })
      .reduce((a, v) => {
        return { ...a, [v.key]: v.data };
      }, {});
  }

  private _mapClientDetailUpdate(data: any) {
    return new ClientDetailUpdate({
      clientInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.client_info
      ),
      taxInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.tax_info
      ),
      contractInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.contract_info
      ),
      bank: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.bank
      ),
      bankSettlement: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.bank_settlement
      ),
      billingInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.billing_info
      ),
      contactInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.contact_info
      ),
      payment: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.payment_method_info
      ),
      clientParentInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.client_parent_info
      ),
      codInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.cod_info
      ),
      returnInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.return_info
      ),
      returnConfigurationInfo: this._mapClientDetailUpdateKeyItem(
        data.client_meta?.detail_update?.return_configuration_info
      )
    });
  }

  private _mapClientDetailUpdateItem(data: any) {
    return new ClientDetailUpdateItem({
      currentVal: data.current_val,
      prevVal: data.prev_val,
      updatedByName: data.updater_name,
      updatedById: data.updater_id,
      updatedAt: parseInt(data.updated_at || "0")
    });
  }

  private _getCodDistrict(data: any) {
    return data.client_branch_cod_return_receiver_district_id
      ? new ClientLocation({
          id: data.client_branch_cod_return_receiver_district_id,
          code: data.client_branch_cod_return_receiver_district_code,
          name: data.client_branch_cod_return_receiver_district_name,
          cityCode: data.client_branch_cod_return_receiver_city_code
        })
      : "";
  }

  public _mapClientCodReject(data: any) {
    return new ClientCod({
      clientCodName: data.client_branch_cod_return_receiver_name,
      clientCodAddress: data.client_branch_cod_return_receiver_address,
      clientCodDistrict: this._getCodDistrict(data),
      clientCodPhone: data.client_branch_cod_return_receiver_phone.substr(2),
      clientCodCommission: data.client_branch_cod_fee
    });
  }

  public _mapClientCodRejectAsDefault(data: any) {
    return new ClientCodAsDefault({
      defaultCodAddress: data.client_branch_cod_return_receiver_address,
      defaultCodDistrict: this._getCodDistrict(data),
      defaultCodName: data.client_branch_cod_return_receiver_name,
      defaultCodPhone: data.client_branch_cod_return_receiver_phone
    });
  }

  private getSaveValueOrString(value: any) {
    return value || "";
  }

  public convertChangeDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertSuccessProcessDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2(convertSnakeToCamel(data));
  }

  public convertClientRateHistoryDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data.meta?.page,
        limit: data.meta?.limit,
        totalData: data.meta?.total_records
      }),
      data:
        data.data?.map(
          (item: any) => new ClientRateHistory(convertSnakeToCamel(item))
        ) || []
    });
  }

  public convertManagingCountryListFromApi(result: AxiosResponse<any>) {
    const { data } = result;
    const list: any[] = data.data || [];
    return list.map((item: any) => ({
      id: item.client_managing_country_id,
      code: item.client_managing_country_code,
      name: item.client_managing_country_name,
      value: item.client_managing_country_code
    }));
  }
}
